import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Hidden from "@insite/mobius/Hidden";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import Check from "@insite/mobius/Icons/Check";
import ChevronDown from "@insite/mobius/Icons/ChevronDown";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { COLORS, TYPOGRAPHY } from "@tSurya/theme/design";
import React, { FC, useState } from "react";
import { css } from "styled-components";

/**
 * Default labels for the checkout wizard. Kept in the component so every
 * instance stays in sync across pages. Override via the `steps` prop if needed.
 */
export const DEFAULT_CHECKOUT_STEPS = ["Shipping Address", "Shipping Method", "Billing & Payment", "Review & Submit"];

type StepState = "completed" | "active" | "upcoming";

interface SurCheckoutStepperProps {
    /** 1-based index of the currently active step. */
    activeStep: number;
    /** Optional label list. Defaults to {@link DEFAULT_CHECKOUT_STEPS}. */
    steps?: string[];
    /** Steps that have been visited and can therefore be revisited. */
    visitedSteps?: ReadonlySet<number>;
    /** Called when a completed or visited step is selected. */
    onStepClick?: (step: number) => void;
    styles?: SurCheckoutStepperStyles;
}

export interface SurCheckoutStepperStyles {
    wrapper?: InjectableCss;
    desktopWrapper?: InjectableCss;
    desktopList?: InjectableCss;
    desktopStep?: InjectableCss;
    stepButton?: InjectableCss;
    connector?: InjectableCss;
    circle?: InjectableCss;
    circleNumber?: TypographyPresentationProps;
    checkIcon?: IconPresentationProps;
    stepNumberText?: TypographyPresentationProps;
    stepLabelText?: TypographyPresentationProps;
    mobileWrapper?: InjectableCss;
    mobileHeader?: InjectableCss;
    mobileSummary?: InjectableCss;
    mobileLabelText?: TypographyPresentationProps;
    expandButton?: InjectableCss;
    expandIcon?: IconPresentationProps;
    mobileList?: InjectableCss;
    mobileListStep?: InjectableCss;
    mobileStepButton?: InjectableCss;
}

export const surCheckoutStepperStyles: SurCheckoutStepperStyles = {
    wrapper: {
        css: css`
            width: 100%;
            margin-top: 20px;
        `,
    },
    desktopWrapper: {
        css: css`
            width: 100%;
        `,
    },
    desktopList: {
        css: css`
            display: flex;
            align-items: flex-start;
            justify-content: space-between;
            list-style: none;
            margin: 0;
            padding: 0;
        `,
    },
    desktopStep: {
        css: css`
            display: flex;
            flex-direction: column;
            align-items: center;
            flex: 1 1 0;
            position: relative;
            text-align: center;
        `,
    },
    stepButton: {
        css: css`
            display: flex;
            flex-direction: column;
            align-items: center;
            position: relative;
            z-index: 1;
            padding: 0;
            border: 0;
            background: transparent;
            color: inherit;
            font: inherit;

            &:enabled {
                cursor: pointer;
            }
        `,
    },
    connector: {
        css: css`
            position: absolute;
            top: 20px;
            left: calc(50% + 24px);
            width: calc(100% - 48px);
            height: 2px;
            z-index: 0;
        `,
    },
    circle: {
        css: css`
            display: flex;
            align-items: center;
            justify-content: center;
            width: 40px;
            height: 40px;
            border-radius: 50%;
            z-index: 1;
            box-sizing: border-box;
        `,
    },
    circleNumber: {
        css: css`
            font-weight: ${TYPOGRAPHY.weight.mediumbold};
            font-size: 16px;
            line-height: 1;
        `,
    },
    checkIcon: {
        size: 20,
        color: COLORS.white,
    },
    stepNumberText: {
        css: css`
            margin-top: 12px;
            font-weight: ${TYPOGRAPHY.weight.mediumbold};
            font-size: 15px;
            line-height: 1;
        `,
    },
    stepLabelText: {
        css: css`
            margin-top: 6px;
            font-size: 14px;
            line-height: 1.2;
        `,
    },
    mobileWrapper: {
        css: css`
            width: 100%;
        `,
    },
    mobileHeader: {
        css: css`
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 12px;
        `,
    },
    mobileSummary: {
        css: css`
            display: flex;
            align-items: center;
            gap: 12px;
        `,
    },
    mobileLabelText: {
        css: css`
            font-weight: ${TYPOGRAPHY.weight.mediumbold};
            font-size: 16px;
            line-height: 1;
        `,
    },
    expandButton: {
        css: css`
            display: flex;
            align-items: center;
            justify-content: center;
            background: transparent;
            border: none;
            padding: 8px;
            cursor: pointer;
            color: ${COLORS.text.main};
        `,
    },
    mobileList: {
        css: css`
            list-style: none;
            margin: 12px 0 0;
            padding: 0 0 0 20px;
            display: flex;
            flex-direction: column;
            gap: 10px;
        `,
    },
    mobileListStep: {
        css: css`
            display: flex;
            align-items: center;
            gap: 10px;
            transform: scale(0.85);
            transform-origin: left center;
        `,
    },
    mobileStepButton: {
        css: css`
            display: flex;
            align-items: center;
            gap: 10px;
            padding: 0;
            border: 0;
            background: transparent;
            color: inherit;
            font: inherit;

            &:enabled {
                cursor: pointer;
            }
        `,
    },
};

const getStepState = (stepIndex: number, activeStep: number): StepState => {
    if (stepIndex < activeStep) {
        return "completed";
    }
    if (stepIndex === activeStep) {
        return "active";
    }
    return "upcoming";
};

const circleColors = (state: StepState) => {
    switch (state) {
        case "completed":
            return css`
                background-color: ${COLORS.success};
                border: 2px solid ${COLORS.success};
                color: ${COLORS.white};
            `;
        case "active":
            return css`
                background-color: ${COLORS.primary.base};
                border: 2px solid ${COLORS.primary.base};
                color: ${COLORS.primary.contrast};
            `;
        default:
            return css`
                background-color: transparent;
                border: 2px solid ${COLORS.border.main};
                color: ${COLORS.text.disabled};
            `;
    }
};

const connectorColor = (state: StepState) =>
    state === "completed"
        ? css`
              background-color: ${COLORS.success};
          `
        : css`
              background-color: ${COLORS.border.main};
          `;

const StepCircle: FC<{ state: StepState; stepIndex: number; styles: SurCheckoutStepperStyles }> = ({ state, stepIndex, styles }) => (
    <StyledWrapper
        {...styles.circle}
        css={css`
            ${styles.circle?.css}
            ${circleColors(state)}
        `}
    >
        {state === "completed" ? (
            <Icon {...styles.checkIcon} src={Check} />
        ) : (
            <Typography {...styles.circleNumber} aria-hidden="true">
                {stepIndex}
            </Typography>
        )}
    </StyledWrapper>
);

const SurCheckoutStepper: FC<SurCheckoutStepperProps> = ({
    activeStep,
    steps = DEFAULT_CHECKOUT_STEPS,
    visitedSteps,
    onStepClick,
    styles = surCheckoutStepperStyles,
}) => {
    const [expanded, setExpanded] = useState(false);

    const activeIndex = Math.min(Math.max(activeStep, 1), steps.length);
    const activeLabel = steps[activeIndex - 1];

    const stateLabel = (state: StepState) => (state === "completed" ? "completed" : state === "active" ? "current step" : "upcoming");

    return (
        <StyledWrapper {...styles.wrapper} role="navigation" aria-label="Checkout progress" data-testid="checkout-stepper">
            {/* Desktop - hidden below md (shown from md up) */}
            <Hidden below="md">
                <StyledWrapper {...styles.desktopWrapper}>
                    <StyledWrapper as="ol" {...styles.desktopList}>
                        {steps.map((label, index) => {
                            const stepIndex = index + 1;
                            const state = getStepState(stepIndex, activeIndex);
                            const isClickable = !!onStepClick && (state === "completed" || stepIndex === activeIndex || visitedSteps?.has(stepIndex));
                            const isLast = stepIndex === steps.length;
                            return (
                                <StyledWrapper as="li" key={label} {...styles.desktopStep} aria-current={state === "active" ? "step" : undefined}>
                                    {!isLast && (
                                        <StyledWrapper
                                            {...styles.connector}
                                            css={css`
                                                ${styles.connector?.css}
                                                ${connectorColor(state)}
                                            `}
                                            aria-hidden="true"
                                        />
                                    )}
                                    <StyledWrapper
                                        as="button"
                                        type="button"
                                        {...styles.stepButton}
                                        disabled={!isClickable}
                                        onClick={() => isClickable && onStepClick?.(stepIndex)}
                                    >
                                        <StepCircle state={state} stepIndex={stepIndex} styles={styles} />
                                        <Typography {...styles.stepNumberText}>
                                            <VisuallyHidden>{`${stateLabel(state)}`}</VisuallyHidden>
                                        </Typography>
                                        <Typography
                                            {...styles.stepLabelText}
                                            css={css`
                                                ${styles.stepLabelText?.css}
                                                ${isClickable &&
                                                css`
                                                    &:hover {
                                                        text-decoration: underline;
                                                    }
                                                `}
                                            `}
                                        >
                                            {label}
                                        </Typography>
                                    </StyledWrapper>
                                </StyledWrapper>
                            );
                        })}
                    </StyledWrapper>
                </StyledWrapper>
            </Hidden>

            {/* Mobile - hidden above sm (shown up to sm) */}
            <Hidden above="sm">
                <StyledWrapper {...styles.mobileWrapper}>
                    <StyledWrapper {...styles.mobileHeader}>
                        <StyledWrapper {...styles.mobileSummary}>
                            <StepCircle state="active" stepIndex={activeIndex} styles={styles} />
                            <Typography {...styles.mobileLabelText}>{activeLabel}</Typography>
                        </StyledWrapper>
                        <StyledWrapper
                            as="button"
                            type="button"
                            {...styles.expandButton}
                            onClick={() => setExpanded(prev => !prev)}
                            aria-expanded={expanded}
                            aria-label={expanded ? "Hide all steps" : "Show all steps"}
                        >
                            <Icon
                                {...styles.expandIcon}
                                src={ChevronDown}
                                css={css`
                                    transition: transform 0.2s ease;
                                    transform: rotate(${expanded ? "180deg" : "0deg"});
                                `}
                            />
                        </StyledWrapper>
                    </StyledWrapper>
                    {expanded && (
                        <StyledWrapper as="ol" {...styles.mobileList}>
                            {steps.map((label, index) => {
                                const stepIndex = index + 1;
                                const state = getStepState(stepIndex, activeIndex);
                                const isClickable = !!onStepClick && (state === "completed" || stepIndex === activeIndex || visitedSteps?.has(stepIndex));
                                return (
                                    <StyledWrapper as="li" key={label} {...styles.mobileListStep} aria-current={state === "active" ? "step" : undefined}>
                                        <StyledWrapper
                                            as="button"
                                            type="button"
                                            {...styles.mobileStepButton}
                                            disabled={!isClickable}
                                            onClick={() => isClickable && onStepClick?.(stepIndex)}
                                        >
                                            <StepCircle state={state} stepIndex={stepIndex} styles={styles} />
                                            <Typography
                                                {...styles.mobileLabelText}
                                                css={css`
                                                    ${styles.mobileLabelText?.css}
                                                    ${isClickable &&
                                                    css`
                                                        &:hover {
                                                            text-decoration: underline;
                                                        }
                                                    `}
                                                `}
                                            >
                                                {label}
                                                <VisuallyHidden>{`, ${stateLabel(state)}`}</VisuallyHidden>
                                            </Typography>
                                        </StyledWrapper>
                                    </StyledWrapper>
                                );
                            })}
                        </StyledWrapper>
                    )}
                </StyledWrapper>
            </Hidden>
        </StyledWrapper>
    );
};

export default SurCheckoutStepper;
